import { ArrowRight } from "lucide-react";
import { scrollToId } from "../../lib/brand";
import { CmykBar, Crosshair, SectionLabel } from "../Marks";
import { MaskLine, Reveal } from "../Reveal";

const PILLARS = [
  {
    num: "01",
    word: "CREATE",
    sub: "PRODUCTION ART",
    lead: "Great artwork deserves more than a screen. It deserves to be made.",
    body: "No Glyph Studios helps artists, creators and businesses take their artwork from concept to finished product. Whether your design needs a little production help or is ready to print, we can help prepare the artwork and bring it to life on apparel and merchandise.",
    extra:
      "Our services include artwork consultation, design cleanup, resizing and upscaling, reworking or correcting AI-generated artwork, background removal, halftone preparation when needed, and production-ready file setup using Adobe Illustrator and Adobe Photoshop—along with apparel printing and merchandise production.",
    closer:
      "You bring the artwork. We help make it production-ready—and make it real.",
    cta: "GET ARTWORK HELP",
    testid: "pillar-create",
    accent: "var(--ng-blue-bright)",
  },
  {
    num: "02",
    word: "PRODUCE",
    sub: "APPAREL & MERCHANDISE",
    lead: "Take your artwork from the screen to a finished product.",
    body: "No Glyph Studios provides apparel and merchandise production support with attention to artwork quality, print preparation, placement and finished presentation.",
    cta: "START A PRINT PROJECT",
    testid: "pillar-produce",
    accent: "var(--ng-red)",
  },
  {
    num: "03",
    word: "SELL",
    sub: "INDEPENDENT ART",
    lead: "Independent artwork deserves more ways to reach an audience.",
    body: "No Glyph Studios is building a future platform for original products, artist collaborations and independent artwork transformed into merchandise people can wear, use and discover.",
    cta: "WORK WITH NO GLYPH",
    testid: "pillar-sell",
    accent: "#f8f8f5",
  },
];

export const Pillars = () => (
  <section
    id="what-we-do"
    data-testid="section-what-we-do"
    className="relative border-b border-[#e2e2da] bg-[#f8f8f5] px-5 py-24 text-[#0a0a0a] sm:px-8 sm:py-32"
  >
    <div className="mx-auto max-w-[1600px]">
      <div className="flex flex-wrap items-end justify-between gap-8 border-b border-[#0a0a0a] pb-8">
        <SectionLabel index="01">WHAT WE DO</SectionLabel>
        <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-neutral-500">
          CREATE&nbsp;&nbsp;·&nbsp;&nbsp;PRODUCE&nbsp;&nbsp;·&nbsp;&nbsp;SELL
        </p>
      </div>

      <div>
        {PILLARS.map((p, i) => (
          <article
            key={p.word}
            data-testid={p.testid}
            className="group relative grid gap-8 border-b border-[#d8d8ce] py-14 lg:grid-cols-12 lg:gap-10 lg:py-20"
          >
            <div className="lg:col-span-5">
              <div className="flex items-baseline gap-4">
                <span className="font-mono-tech text-xs tracking-[0.2em] text-neutral-400">
                  {p.num}
                </span>
                <h3 className="display-xl text-[17vw] leading-[0.8] sm:text-[12vw] lg:text-[7.4vw]">
                  <MaskLine delay={0.05 * i}>
                    <span
                      className="inline-block transition-colors duration-500"
                      style={{
                        color: i === 2 ? "#0a0a0a" : p.accent,
                      }}
                    >
                      {p.word}
                    </span>
                  </MaskLine>
                </h3>
              </div>
              <div className="mt-4 flex items-center gap-4">
                <span
                  className="h-px w-16"
                  style={{ background: i === 2 ? "#0a0a0a" : p.accent }}
                />
                <h4 className="font-display text-xl font-bold uppercase tracking-tight sm:text-2xl">
                  {p.sub}
                </h4>
              </div>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal delay={0.05}>
                <p className="font-display text-2xl font-bold uppercase leading-tight tracking-tight sm:text-3xl">
                  {p.lead}
                </p>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-neutral-700">
                  {p.body}
                </p>
                {p.extra && (
                  <p className="mt-4 max-w-2xl text-base leading-relaxed text-neutral-700">
                    {p.extra}
                  </p>
                )}
                {p.closer && (
                  <p
                    className="mt-7 max-w-xl border-l-2 pl-5 font-display text-xl font-extrabold uppercase leading-tight tracking-tight sm:text-2xl"
                    style={{ borderColor: "var(--ng-red)" }}
                  >
                    {p.closer}
                  </p>
                )}
                <button
                  data-testid={`${p.testid}-cta`}
                  onClick={() => scrollToId("inquiry")}
                  className="group/btn mt-9 inline-flex items-center gap-3 border-b-2 border-[#0a0a0a] pb-2 font-display text-base font-extrabold uppercase tracking-wide transition-colors hover:border-[#e62b2b] hover:text-[#e62b2b]"
                >
                  {p.cta}
                  <ArrowRight
                    size={17}
                    className="transition-transform duration-300 group-hover/btn:translate-x-1.5"
                  />
                </button>
              </Reveal>
            </div>

            <Crosshair
              size={16}
              className="absolute right-0 top-10 hidden text-neutral-300 lg:block"
            />
          </article>
        ))}
      </div>

      <div className="mt-12 flex flex-wrap items-center gap-6">
        <p className="font-display text-lg font-extrabold uppercase tracking-tight sm:text-2xl">
          GOOD ARTWORK + GOOD PRODUCTION ={" "}
          <span style={{ color: "var(--ng-red)" }}>BETTER FINISHED PRODUCTS</span>
        </p>
        <div className="w-28">
          <CmykBar />
        </div>
      </div>
    </div>
  </section>
);
